// "This and all future" edits of a recurring activity's timing (docs/features/recurring-activities.md).
// Every occurrence keeps its own date; only the time of day changes, and enrolment and the
// cancellation deadline stay at the same distance before the start as on the edited occurrence.
// The client applies this itself instead of sending absolute dates to
// activities-updateFutureRecurrentActivities, which would copy one occurrence's dates to all.
//
// Occurrences store 'YYYY-MM-DDTHH:mm' (UTC wall clock); the series doc (recurrents/{id}) stores
// start/end as 'HH:mm' and the entry/cancellation times as minutes before the start.

import moment from 'moment'

const FORMAT = 'YYYY-MM-DDTHH:mm'

// The fields a timing edit consists of; any of them edited means the timing changed.
export const TIMING_FIELDS = ['start', 'end', 'entrystart', 'entryend', 'cancellationend']

const isEmpty = (value) => value === null || value === undefined || value === ''

const minutesBefore = (start, value) => {
  if (isEmpty(value)) return null
  const at = moment.utc(value, FORMAT)
  return at.isValid() ? start.diff(at, 'minutes') : null
}

// originalStart / edits: an entry or cancellation time the user did not touch keeps its distance
// from the occurrence's ORIGINAL start - moving the start by five hours moves enrolment by five
// hours too. One the user did edit is taken as set, relative to the new start.
export const timingFromEdited = (edited, { originalStart, edits } = {}) => {
  const start = moment.utc(edited.start, FORMAT)
  const end = moment.utc(edited.end, FORMAT)
  if (!start.isValid() || !end.isValid()) return null
  const original = originalStart ? moment.utc(originalStart, FORMAT) : null
  const relative = (field) => {
    const base = !edits || edits[field] || !original || !original.isValid() ? start : original
    return minutesBefore(base, edited[field])
  }
  return {
    startTime: start.format('HH:mm'),
    endTime: end.format('HH:mm'),
    // 0 when start and end are the same moment; then end is set to the start
    duration: Math.max(0, end.diff(start, 'minutes')),
    entrystart: relative('entrystart'),
    entryend: relative('entryend'),
    cancellationend: relative('cancellationend'),
  }
}

// The occurrence with the new times on its own date. The end keeps the edited duration, so an
// occurrence that ran past midnight still does.
export const applyTiming = (occurrence, timing) => {
  const date = moment.utc(occurrence.start, FORMAT)
  if (!timing || !date.isValid()) return occurrence
  const [hours, minutes] = timing.startTime.split(':').map(Number)
  const start = date.clone().hour(hours).minute(minutes)
  const before = (value) => (value === null || value === undefined ? null : start.clone().subtract(value, 'minutes').format(FORMAT))
  const next = {
    ...occurrence,
    start: start.format(FORMAT),
    end: start.clone().add(timing.duration, 'minutes').format(FORMAT),
  }
  // an occurrence without a value keeps it empty rather than inventing one
  if (timing.entrystart !== null) next.entrystart = before(timing.entrystart)
  if (timing.entryend !== null) next.entryend = before(timing.entryend)
  next.cancellationend = before(timing.cancellationend)
  return next
}

// The same timing for the series doc, so occurrences generated later get it too.
export const templateTiming = (timing) => {
  const fields = { start: timing.startTime, end: timing.endTime, cancellationend: timing.cancellationend }
  if (timing.entrystart !== null) fields.entrystart = timing.entrystart
  if (timing.entryend !== null) fields.entryend = timing.entryend
  return fields
}

// Occurrences from the edited one on, by their original start.
export const futureOccurrences = (occurrences, fromStart) =>
  (occurrences || []).filter((o) => o && o.start && o.state !== 'deleted' && String(o.start) >= String(fromStart))
